/**
 * Hidden Markov Model (HMM) Map Matching Service
 * Matches GPS traces to road networks using HMM approach
 */

import osmService from './osmService';

class HMMMapMatching {
  constructor() {
    this.osmService = osmService;
    this.gpsNoiseVariance = 10; // meters - GPS accuracy variance
    this.transitionVariance = 50; // meters - expected movement variance
    this.maxCandidateDistance = 100; // meters - max distance to consider candidates
  }

  /**
   * Main HMM map matching function
   * @param {Array} gpsPath - Array of GPS points with {latitude, longitude, timestamp, accuracy}
   * @param {Object} options - Matching options
   */
  async matchPath(gpsPath, options = {}) {
    if (!gpsPath || gpsPath.length === 0) {
      return { matchedPath: [], stats: { totalPoints: 0, matchedPoints: 0, matchRate: 0 } };
    }

    try {
      console.log(`Starting HMM map matching for ${gpsPath.length} GPS points...`);

      // Step 1: Preprocess GPS data
      const cleanedPath = this.preprocessGPSPath(gpsPath);
      console.log(`Preprocessed to ${cleanedPath.length} points`);

      // Step 2: Get road network
      const bounds = this.getPathBounds(cleanedPath);
      const roadNetwork = await this.osmService.getRoadNetwork(bounds, {
        includeRoads: true,
        includeFootways: true,
        includePaths: true,
        includeSteps: true,
        maxDistance: 200
      });

      if (roadNetwork.ways.length === 0) {
        console.log('No road network found in area');
        return {
          matchedPath: cleanedPath.map(point => ({ ...point, isMatched: false })),
          stats: { totalPoints: cleanedPath.length, matchedPoints: 0, matchRate: 0 }
        };
      }

      console.log(`Found ${roadNetwork.ways.length} road segments`);

      // Step 3: Generate candidates for each GPS point
      const candidates = this.generateCandidates(cleanedPath, roadNetwork);
      console.log(`Generated candidates for ${candidates.length} points`);

      // Step 4: Apply HMM algorithm (Viterbi)
      const bestPath = this.viterbiAlgorithm(candidates, roadNetwork);
      console.log(`HMM algorithm completed`);

      // Step 5: Post-process and create final result
      const matchedPath = this.createMatchedPath(cleanedPath, bestPath, roadNetwork);

      // Calculate statistics
      const matchedPoints = matchedPath.filter(p => p.isMatched).length;
      const stats = {
        totalPoints: cleanedPath.length,
        matchedPoints: matchedPoints,
        matchRate: (matchedPoints / cleanedPath.length) * 100,
        totalRoads: roadNetwork.ways.length,
        averageConfidence: this.calculateAverageConfidence(matchedPath),
        pathTypes: this.getPathTypeStats(matchedPath)
      };

      console.log(`HMM matching complete: ${matchedPoints}/${cleanedPath.length} points matched (${stats.matchRate.toFixed(1)}%)`);
      
      return { matchedPath, stats };
    } catch (error) {
      console.error('HMM map matching failed:', error);
      return {
        matchedPath: gpsPath.map(point => ({ ...point, isMatched: false })),
        stats: { totalPoints: gpsPath.length, matchedPoints: 0, matchRate: 0, error: error.message }
      };
    }
  }

  /**
   * Preprocess GPS data to remove noise and outliers
   */
  preprocessGPSPath(gpsPath) {
    if (gpsPath.length < 2) return gpsPath;

    const cleaned = [gpsPath[0]]; // Always keep first point

    for (let i = 1; i < gpsPath.length; i++) {
      const current = gpsPath[i];
      const previous = cleaned[cleaned.length - 1];

      // Filter by accuracy
      if (current.accuracy && current.accuracy > 50) continue; // Skip low accuracy points

      // Filter by distance (minimum 5 meters)
      const distance = this.calculateDistance(
        previous.latitude, previous.longitude,
        current.latitude, current.longitude
      );
      if (distance < 5) continue;

      // Filter by speed (maximum 200 km/h)
      const timeDiff = (current.timestamp - previous.timestamp) / 1000; // seconds
      if (timeDiff > 0) {
        const speed = (distance / timeDiff) * 3.6; // km/h
        if (speed > 200) continue;
      }

      cleaned.push(current);
    }

    return cleaned;
  }

  /**
   * Generate candidate road segments for each GPS point
   */
  generateCandidates(gpsPath, roadNetwork) {
    return gpsPath.map((point, index) => {
      const candidates = [];

      for (const way of roadNetwork.ways) {
        // Find closest point on this road segment
        const closestPoint = this.findClosestPointOnWay(point, way);
        
        if (closestPoint.distance <= this.maxCandidateDistance) {
          candidates.push({
            way: way,
            point: closestPoint.point,
            distance: closestPoint.distance,
            confidence: this.calculateEmissionProbability(closestPoint.distance, point.accuracy)
          });
        }
      }

      // Sort by confidence (highest first)
      candidates.sort((a, b) => b.confidence - a.confidence);

      return {
        gpsPoint: point,
        index: index,
        candidates: candidates.slice(0, 10) // Keep top 10 candidates
      };
    });
  }

  /**
   * Find closest point on a road way
   */
  findClosestPointOnWay(gpsPoint, way) {
    let minDistance = Infinity;
    let closestPoint = null;

    for (let i = 0; i < way.geometry.length - 1; i++) {
      const segmentStart = way.geometry[i];
      const segmentEnd = way.geometry[i + 1];
      
      const pointOnSegment = this.projectPointToLineSegment(
        gpsPoint.latitude, gpsPoint.longitude,
        segmentStart.latitude, segmentStart.longitude,
        segmentEnd.latitude, segmentEnd.longitude
      );
      
      const distance = this.calculateDistance(
        gpsPoint.latitude, gpsPoint.longitude,
        pointOnSegment.latitude, pointOnSegment.longitude
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = pointOnSegment;
      }
    }

    return { point: closestPoint, distance: minDistance };
  }

  /**
   * Project point to line segment
   */
  projectPointToLineSegment(px, py, x1, y1, x2, y2) {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) {
      return { latitude: x1, longitude: y1 };
    }

    let param = Math.max(0, Math.min(1, dot / lenSq));

    return {
      latitude: x1 + param * C,
      longitude: y1 + param * D
    };
  }

  /**
   * Calculate emission probability (GPS point -> road candidate)
   */
  calculateEmissionProbability(distance, gpsAccuracy) {
    const accuracy = gpsAccuracy || this.gpsNoiseVariance;
    const variance = Math.max(accuracy * accuracy, this.gpsNoiseVariance * this.gpsNoiseVariance);
    
    // Gaussian probability
    return Math.exp(-(distance * distance) / (2 * variance));
  }

  /**
   * Calculate transition probability (road -> road)
   */
  calculateTransitionProbability(fromCandidate, toCandidate, timeDiff) {
    if (!fromCandidate || !toCandidate) return 0;

    const fromWay = fromCandidate.way;
    const toWay = toCandidate.way;

    // If same road, high probability
    if (fromWay.id === toWay.id) {
      return 0.9;
    }

    // Calculate distance between road segments
    const distance = this.calculateDistance(
      fromCandidate.point.latitude, fromCandidate.point.longitude,
      toCandidate.point.latitude, toCandidate.point.longitude
    );

    // Calculate expected distance based on time
    const expectedDistance = timeDiff * 1.4; // Assume 1.4 m/s walking speed
    const distanceDiff = Math.abs(distance - expectedDistance);

    // Transition probability based on distance difference
    const variance = this.transitionVariance * this.transitionVariance;
    return Math.exp(-(distanceDiff * distanceDiff) / (2 * variance));
  }

  /**
   * Viterbi algorithm for HMM
   */
  viterbiAlgorithm(candidates, roadNetwork) {
    if (candidates.length === 0) return [];

    const n = candidates.length;
    const bestPath = new Array(n);
    const probabilities = new Array(n);

    // Initialize first step
    probabilities[0] = candidates[0].candidates.map(candidate => ({
      candidate: candidate,
      probability: candidate.confidence,
      previous: null
    }));

    // Forward pass
    for (let t = 1; t < n; t++) {
      const currentCandidates = candidates[t].candidates;
      const previousProbabilities = probabilities[t - 1];
      const timeDiff = (candidates[t].gpsPoint.timestamp - candidates[t - 1].gpsPoint.timestamp) / 1000;

      probabilities[t] = currentCandidates.map(currentCandidate => {
        let maxProbability = 0;
        let bestPrevious = null;

        for (const prevState of previousProbabilities) {
          const transitionProb = this.calculateTransitionProbability(
            prevState.candidate, currentCandidate, timeDiff
          );
          const totalProbability = prevState.probability * transitionProb * currentCandidate.confidence;

          if (totalProbability > maxProbability) {
            maxProbability = totalProbability;
            bestPrevious = prevState;
          }
        }

        return {
          candidate: currentCandidate,
          probability: maxProbability,
          previous: bestPrevious
        };
      });
    }

    // Backward pass - find best path
    let bestFinalState = probabilities[n - 1].reduce((best, current) => 
      current.probability > best.probability ? current : best
    );

    // Reconstruct path
    const path = [];
    let current = bestFinalState;
    
    for (let t = n - 1; t >= 0; t--) {
      path[t] = current;
      current = current.previous;
    }

    return path;
  }

  /**
   * Create final matched path from HMM results
   */
  createMatchedPath(gpsPath, bestPath, roadNetwork) {
    return gpsPath.map((point, index) => {
      const hmmResult = bestPath[index];
      
      if (hmmResult && hmmResult.candidate) {
        return {
          ...point,
          isMatched: true,
          matchedRoad: hmmResult.candidate.way,
          matchedPoint: hmmResult.candidate.point,
          roadDistance: hmmResult.candidate.distance,
          confidence: hmmResult.probability,
          pathType: hmmResult.candidate.way.type,
          pathName: hmmResult.candidate.way.name
        };
      } else {
        return {
          ...point,
          isMatched: false
        };
      }
    });
  }

  /**
   * Calculate average confidence of matched points
   */
  calculateAverageConfidence(matchedPath) {
    const matchedPoints = matchedPath.filter(p => p.isMatched && p.confidence);
    if (matchedPoints.length === 0) return 0;
    
    const totalConfidence = matchedPoints.reduce((sum, p) => sum + p.confidence, 0);
    return totalConfidence / matchedPoints.length;
  }

  /**
   * Get statistics about path types
   */
  getPathTypeStats(matchedPath) {
    const typeStats = {};
    
    matchedPath
      .filter(p => p.isMatched)
      .forEach(p => {
        const type = p.pathType || 'unknown';
        typeStats[type] = (typeStats[type] || 0) + 1;
      });

    return typeStats;
  }

  /**
   * Get bounds of GPS path
   */
  getPathBounds(gpsPath) {
    const lats = gpsPath.map(p => p.latitude);
    const lons = gpsPath.map(p => p.longitude);
    
    return {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons)
    };
  }

  /**
   * Calculate distance between two points in meters
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Test the HMM map matching service
   */
  async testService() {
    console.log('Testing HMM Map Matching Service...');
    
    // Create a test GPS path (walking around Central Park, NYC)
    const testPath = [
      { latitude: 40.7829, longitude: -73.9654, timestamp: Date.now(), accuracy: 5 },
      { latitude: 40.7830, longitude: -73.9655, timestamp: Date.now() + 1000, accuracy: 8 },
      { latitude: 40.7831, longitude: -73.9656, timestamp: Date.now() + 2000, accuracy: 6 },
      { latitude: 40.7832, longitude: -73.9657, timestamp: Date.now() + 3000, accuracy: 7 },
      { latitude: 40.7833, longitude: -73.9658, timestamp: Date.now() + 4000, accuracy: 5 }
    ];

    try {
      const result = await this.matchPath(testPath);
      console.log('HMM map matching test result:', result);
      return result;
    } catch (error) {
      console.error('HMM map matching test failed:', error);
      return null;
    }
  }
}

export default new HMMMapMatching();
